/**
 * templates/categories/reader.js - Reader Presentation Templates
 * Location: /app/frontend/src/admin/page_builder/templates/categories/
 * 
 * Reader templates define section structure. Device behavior and navigation
 * mechanics are handled in the reader layer (Phase 3).
 * 
 * These are STUBBED - full implementation in Phase 3.
 */

import { LayoutPreset } from '../../layout/presets';
import { BackgroundType } from '../../layout/backgrounds';
import { CardVariant } from '../../cards/variants';

/**
 * Chapter List - Vertical list of chapters/episodes
 * Use cases: Comic series page, novel page, episode selection
 */
export const chapter_list = {
  id: 'chapter_list',
  name: 'Chapter List',
  category: 'reader',
  description: 'Vertical list of chapters or episodes with progress indicators.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.READING,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID],
  allowedCardVariants: [CardVariant.READING],
  maxBlocks: 1, // Only one chapter list per page
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_chapter_list',
  defaultProps: {
    heading: 'Chapters',
    showHeading: true,
    seriesId: '', // Bound at render time
    sortOrder: 'asc', // 'asc', 'desc'
    showProgress: true,
    showPublishDate: true,
    showPageCount: true,
    groupByVolume: false,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'sortOrder', label: 'Sort Order', type: 'select', options: ['asc', 'desc'] },
    { key: 'showProgress', label: 'Show Progress', type: 'boolean' },
    { key: 'showPublishDate', label: 'Show Publish Date', type: 'boolean' },
    { key: 'showPageCount', label: 'Show Page Count', type: 'boolean' },
    { key: 'groupByVolume', label: 'Group by Volume', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/series/:seriesId/episodes',
    params: ['seriesId', 'sortOrder'],
  },
};

/**
 * Vertical Reader Stack - Full-page vertical scroll reader
 * Use cases: Webtoon-style reading, long-form comics
 * 
 * STUB: Device behavior in Phase 3
 */
export const vertical_reader_stack = {
  id: 'vertical_reader_stack',
  name: 'Vertical Reader',
  category: 'reader',
  description: 'Full-page vertical scroll reader for webtoon-style content.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { 
    type: BackgroundType.SOLID,
    config: { color: '#000000' }
  },
  cardVariant: null,
  
  // Template constraints - Reader sections are highly constrained
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.SOLID],
  maxBlocks: 1, // Only one reader per page
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.SOLID },
  
  blockType: 'section_vertical_reader',
  defaultProps: {
    episodeId: '', // Bound at render time
    showProgress: true,
    showPageNumbers: false,
    fitWidth: true, // Fit pages to viewport width
    spacing: 0, // Gap between pages (px)
    showNavOverlay: true, // Tap zones for prev/next
  },
  
  editableFields: [
    { key: 'showProgress', label: 'Show Progress Bar', type: 'boolean' },
    { key: 'showPageNumbers', label: 'Show Page Numbers', type: 'boolean' },
    { key: 'fitWidth', label: 'Fit Width', type: 'boolean' },
    { key: 'spacing', label: 'Page Spacing (px)', type: 'number', min: 0, max: 20 },
    { key: 'showNavOverlay', label: 'Show Nav Overlay', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/episodes/:episodeId/pages',
    params: ['episodeId'],
  },
  
  // Phase 3: Device behavior hints (not implemented yet)
  deviceBehavior: {
    mobile: 'scroll-vertical',
    desktop: 'scroll-vertical',
  },
};

/**
 * Horizontal Reader - Landscape/side-scroll reader
 * Use cases: Traditional comic reading, panel-by-panel navigation
 * 
 * STUB: Device behavior (landscape detection, side-scroll) in Phase 3
 */
export const horizontal_reader = {
  id: 'horizontal_reader',
  name: 'Horizontal Reader',
  category: 'reader',
  description: 'Landscape/side-scroll reader for panel-by-panel or page-by-page reading.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { 
    type: BackgroundType.SOLID,
    config: { color: '#000000' }
  },
  cardVariant: null,
  
  // Template constraints - Reader sections are highly constrained
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.SOLID],
  maxBlocks: 1, // Only one reader per page
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.SOLID },
  
  blockType: 'section_horizontal_reader',
  defaultProps: {
    episodeId: '', // Bound at render time
    mode: 'page', // 'page' (page-by-page) or 'panel' (panel-by-panel)
    showProgress: true,
    showPageNumbers: true,
    showNavArrows: true,
    snapToPage: true, // Snap scroll to page boundaries
    preserveAspectRatio: true,
  },
  
  editableFields: [
    { key: 'mode', label: 'Reading Mode', type: 'select', options: ['page', 'panel'] },
    { key: 'showProgress', label: 'Show Progress', type: 'boolean' },
    { key: 'showPageNumbers', label: 'Show Page Numbers', type: 'boolean' },
    { key: 'showNavArrows', label: 'Show Nav Arrows', type: 'boolean' },
    { key: 'snapToPage', label: 'Snap to Page', type: 'boolean' },
    { key: 'preserveAspectRatio', label: 'Preserve Aspect Ratio', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/episodes/:episodeId/pages',
    params: ['episodeId'],
  },
  
  // Phase 3: Device behavior hints (not implemented yet)
  deviceBehavior: {
    mobile: {
      portrait: 'scroll-vertical', // Falls back to vertical in portrait
      landscape: 'scroll-horizontal', // Primary horizontal experience
    },
    desktop: 'scroll-horizontal',
  },
};

/**
 * Page-by-Page Reader - Traditional single-page navigation
 * Use cases: Traditional manga/comic reading, high-res art viewing
 * 
 * STUB: Navigation mechanics in Phase 3
 */
export const page_by_page_reader = {
  id: 'page_by_page_reader',
  name: 'Page-by-Page Reader',
  category: 'reader',
  description: 'Traditional single-page display with prev/next navigation.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { 
    type: BackgroundType.SOLID,
    config: { color: '#000000' }
  },
  cardVariant: null,
  
  // Template constraints - Reader sections are highly constrained
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.SOLID],
  maxBlocks: 1, // Only one reader per page
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.SOLID },
  
  blockType: 'section_page_by_page_reader',
  defaultProps: {
    episodeId: '', // Bound at render time
    showProgress: true,
    showPageNumbers: true,
    showNavButtons: true, // Prev/Next buttons
    showThumbnailStrip: false, // Optional thumbnail navigation
    fitToViewport: true, // Fit page to viewport
    zoomEnabled: false, // Pinch-to-zoom (disabled by default)
  },
  
  editableFields: [
    { key: 'showProgress', label: 'Show Progress Bar', type: 'boolean' },
    { key: 'showPageNumbers', label: 'Show Page Numbers', type: 'boolean' },
    { key: 'showNavButtons', label: 'Show Nav Buttons', type: 'boolean' },
    { key: 'showThumbnailStrip', label: 'Show Thumbnail Strip', type: 'boolean' },
    { key: 'fitToViewport', label: 'Fit to Viewport', type: 'boolean' },
    { key: 'zoomEnabled', label: 'Enable Zoom', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/episodes/:episodeId/pages',
    params: ['episodeId'],
  },
  
  // Phase 3: Device behavior hints
  deviceBehavior: {
    mobile: 'tap-to-navigate', // Tap edges to navigate
    desktop: 'click-to-navigate', // Click arrows or use keyboard
  },
};

export default {
  chapter_list,
  vertical_reader_stack,
  horizontal_reader,
  page_by_page_reader,
};
